import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Twitter, Copy, Check, Gift, Mail, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { popConfetti, fireworks } from "@/lib/confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNLOCK_THRESHOLD = 200;

/**
 * Renders inside the game-over overlay. Two modes:
 *  - score >= 200 → show discount-unlock email capture; once submitted, show code + share
 *  - score <  200 → show plain "Share your score" row
 */
export default function ShareAndUnlock({ score, rank, accuracy, hits }) {
  const unlocked = score >= UNLOCK_THRESHOLD;
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [claim, setClaim] = useState(null); // {code, discount_pct, message}
  const [copied, setCopied] = useState(false);

  const shareText = claim
    ? `I just scored ${score} on Honk Patrol and unlocked 10% off SECQRE 🟡 — try beating me:`
    : `I scored ${score} on SECQRE's Honk Patrol (${rank.label} ${rank.emoji}). Beat me 👇`;
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/play` : "https://secqre.com/play";

  const copy = async (text, label = "Copied to clipboard") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Couldn't copy — long-press to select & copy manually.");
    }
  };

  const onTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=SECQRE,HonkPatrol`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onCopyScore = () => copy(`${shareText} ${shareUrl}`, "Score copied — paste anywhere.");
  const onCopyCode = () => claim && copy(claim.code, `Code ${claim.code} copied`);

  const submitClaim = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email, score, source: "honk-patrol" });
      setClaim(res.data);
      fireworks();
      popConfetti({ x: 0.5, y: 0.4 });
      toast.success("🏆 Discount unlocked!", { description: `Code ${res.data.code} — saved & ready to use.` });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Couldn't claim — try a different email.");
    } finally {
      setSubmitting(false);
    }
  };

  // SCORE < 200 → simple share row
  if (!unlocked) {
    return (
      <div className="mt-7" data-testid="share-row">
        <div className="text-[11px] uppercase tracking-widest font-bold text-white/60 mb-3">Share your score</div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button onClick={onTwitter} className="inline-flex items-center gap-2 rounded-lg bg-white text-[#0A0A0A] px-4 py-2.5 text-sm font-semibold hover:-translate-y-0.5 transition-all" data-testid="share-twitter">
            <Twitter className="w-4 h-4" /> Post to X / Twitter
          </button>
          <button onClick={onCopyScore} className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-transparent text-white px-4 py-2.5 text-sm font-semibold hover:bg-white/10 hover:-translate-y-0.5 transition-all" data-testid="share-copy">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy score"}
          </button>
        </div>
        <p className="mt-4 text-xs text-white/50">
          <Sparkles className="inline w-3 h-3 mr-1 text-[#FFD600]" />
          Beat <strong className="text-white">{UNLOCK_THRESHOLD}</strong> to unlock 10% off your first SECQRE.
        </p>
      </div>
    );
  }

  // SCORE >= 200 → unlock state
  return (
    <div className="mt-7" data-testid="unlock-block">
      {!claim ? (
        <div className="rounded-2xl bg-[#FFD600] text-[#0A0A0A] p-5 md:p-6 text-left max-w-md mx-auto border-2 border-[#0A0A0A]" data-testid="unlock-claim">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0A0A0A] text-[#FFD600] inline-flex items-center justify-center">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] tracking-widest font-bold uppercase opacity-70">Reward unlocked</div>
              <div className="font-heading font-black text-xl leading-tight">10% off your first SECQRE</div>
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed">
            You just cleared <strong>{score} points</strong> of bad parking. Drop your email and we&apos;ll send the
            code straight to your inbox — and pin it on this screen so you can use it now.
          </p>
          <form onSubmit={submitClaim} className="mt-4 flex gap-2" data-testid="unlock-form">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A0A]/50 pointer-events-none" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@drivers.com"
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-[#0A0A0A] bg-white text-[#0A0A0A] placeholder:text-[#0A0A0A]/40 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/30"
                data-testid="unlock-email"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-50"
              data-testid="unlock-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {submitting ? "Claiming" : "Claim"}
            </button>
          </form>
          <p className="mt-2 text-[11px] opacity-70">One code per email · 60-day expiry · No marketing spam.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-[#FFD600] text-[#0A0A0A] p-5 md:p-6 text-left max-w-md mx-auto border-2 border-[#0A0A0A]" data-testid="unlock-success">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0A0A0A] text-[#FFD600] inline-flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] tracking-widest font-bold uppercase opacity-70">Your discount code</div>
              <div className="font-heading font-black text-2xl leading-tight tracking-tight">{claim.discount_pct}% OFF · SECQRE</div>
            </div>
          </div>

          <button
            onClick={onCopyCode}
            className="mt-4 group w-full inline-flex items-center justify-between gap-2 rounded-lg bg-white border-2 border-dashed border-[#0A0A0A] px-4 py-3 font-mono font-bold text-lg tracking-widest hover:bg-[#fef9c3] transition-colors"
            data-testid="unlock-copy-code"
          >
            <span data-testid="unlock-code">{claim.code}</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-sans font-semibold normal-case tracking-normal opacity-70 group-hover:opacity-100">
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </span>
          </button>

          <a
            href="/pricing"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0A0A0A] text-white px-4 py-3 font-semibold hover:bg-[#1E293B] transition-colors"
            data-testid="unlock-checkout"
          >
            Use code now → <ArrowRight className="w-4 h-4" />
          </a>

          <p className="mt-3 text-[11px] opacity-70 text-center">
            Sent to <strong>{claim.email || email}</strong>. Show this off:
          </p>

          <div className="mt-3 flex items-center justify-center gap-2">
            <button onClick={onTwitter} className="inline-flex items-center gap-2 rounded-lg bg-[#0A0A0A] text-white px-3 py-2 text-xs font-semibold hover:-translate-y-0.5 transition-all" data-testid="unlock-share-twitter">
              <Twitter className="w-3.5 h-3.5" /> Brag on X
            </button>
            <button onClick={onCopyScore} className="inline-flex items-center gap-2 rounded-lg border-2 border-[#0A0A0A] bg-transparent text-[#0A0A0A] px-3 py-2 text-xs font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="unlock-share-copy">
              <Copy className="w-3.5 h-3.5" /> Copy score
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
